import {
  cloneElement,
  ComponentProps,
  FC,
  isValidElement,
  ReactElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';
import { isFunction } from 'lodash-es';
import { usePriorityRendererStore } from '../store/priority-renderer.store';

const NODE_HIDE_STYLES = 'display: none;';
interface PriorityRendererItemWrapperProps<T extends FC<any>> {
  itemName: string;
  groupName: string;
  closeFnProp: keyof ComponentProps<T>;
  children: ReactElement<{ innerRef?: React.LegacyRef<HTMLElement> }> | null | false;
}

/**
 * Priority renderer item wrapper will render the child only if the item is the top priority item in the group
 * @param itemName string - unique name of the item
 * @param groupName string - name of the group
 * @param closeFnProp string - name of the callback method prop that will be called when the item is closed
 * @param children ReactElement - child to be rendered. The child component should support an innerRef prop to detect if child is rendered
 *
 * @example
 * <PriorityRendererItemWrapper<typeof Component1> itemName="item1" groupName="group1" closeFnProp="onClose">
 *  <Component1 />
 * </PriorityRendererItemWrapper>
 *
 */
export const PriorityRendererItemWrapper = <T extends FC<any>>({
  children,
  closeFnProp,
  groupName,
  itemName,
}: PriorityRendererItemWrapperProps<T>) => {
  const { isGroupInitialized, isTopPriorityItem, registerItem, unregisterItem, markForDeprioritization } =
    usePriorityRendererStore();

  const nodeOriginalStyleRef = useRef<string | null>(null);
  const nodeRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    return () => {
      nodeOriginalStyleRef.current = null;
      nodeRef.current = null;
      unregisterItem?.({ groupName, itemName });
    };
  }, [groupName, itemName, unregisterItem]);

  const handleNodeRefMount = useCallback(
    (node: HTMLElement) => {
      if (node) {
        const nodeStyles = node.getAttribute('style');
        if (nodeStyles !== NODE_HIDE_STYLES && nodeOriginalStyleRef.current !== nodeStyles) {
          nodeOriginalStyleRef.current = nodeStyles;
        }
        nodeRef.current = node;
        registerItem?.({ groupName, itemName });
        changeNodeVisibilityStyle({
          node: nodeRef.current,
          isVisible: isTopPriorityItem?.({ groupName, itemName }) || false,
          originalStyle: nodeOriginalStyleRef.current || '',
        });
      }
    },
    [groupName, itemName, registerItem, isTopPriorityItem]
  );

  useLayoutEffect(() => {
    const unsubscribe = usePriorityRendererStore.subscribe(
      (state) => state.groups,
      () => {
        if (!nodeRef.current) {
          return;
        }
        changeNodeVisibilityStyle({
          node: nodeRef.current,
          isVisible: isTopPriorityItem?.({ groupName, itemName }) || false,
          originalStyle: nodeOriginalStyleRef.current || '',
        });
      }
    );

    return () => {
      unsubscribe();
    };
  }, [groupName, itemName, isTopPriorityItem]);

  if (!isGroupInitialized?.(groupName)) {
    return null;
  }

  if (!isValidElement(children)) {
    return children;
  }

  return cloneElement(children, {
    innerRef: handleNodeRefMount,
    [closeFnProp]: (...args: any[]) => {
      // Mark item for deprioritization and call the close function if any
      markForDeprioritization?.({ groupName, itemName });
      closeFnProp in children.props &&
        isFunction(children.props[closeFnProp as keyof typeof children.props]) &&
        (children.props[closeFnProp as keyof typeof children.props] as any)(...args);
    },
  });
};

type ChangeNodeVisibilityProps = {
  node: HTMLElement;
  isVisible: boolean;
  originalStyle: string;
};
const changeNodeVisibilityStyle = ({ node, isVisible, originalStyle }: ChangeNodeVisibilityProps) => {
  if (!node) {
    return;
  }
  if (isVisible) {
    // If the item is visible, restore the original styles
    node.setAttribute('style', originalStyle);
  } else {
    node.setAttribute('style', NODE_HIDE_STYLES);
  }
};
